import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { erNettfeil } from '../api/feil.ts';
import { useHypoteseConfig, useHypoteseMedNummer, type Hypothesis, type HypothesisConfig } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Flate, Fot, Varsel } from './deler.tsx';
import { fyllMal, navnAv } from './lesning.ts';
import { kortDatoAv } from './setning.ts';

/**
 * Kvitteringen (W6) og hypotesen på adressen /h/{nr}. Kvitteringen LOVER noe (DD-07): løftet
 * for Mottatt er helten, ordrett fra leietakerens ordlyd, og nummeret «H-142» står i hjørnet —
 * tildelt i kallet som nettopp gikk. Ingen dato roper, fordi ingen dato er lovet: sporingslinjens
 * siste ledd sier «innen 28. okt · når den tas» i small (DD-28). Fokus står på løftet, så en
 * skjermleser leser det først. «Ferdig» (↵) går til Mine hypoteser.
 *
 * Kvitteringen står bare rett etter «Send inn»: innsendingen navigerer hit med `{ sendt: true }` i
 * historikkens tilstand, og HModus velger kvitteringen på den. Uten tilstanden — åpnet fra «Mine
 * hypoteser», en lenke eller j/k — er /h/{nr} «Hypotesen min» (W8, `HypotesenMin.tsx`). «Se
 * hypotesen» erstatter historikkens innslag med det samme uten tilstanden, så tilbake-knappen ikke
 * går til kvitteringen igjen.
 */

export const nummerTekst = (n: number | undefined) => (n === undefined ? 'Utkast' : `H-${n}`);

/** Løftet med leietakerens plassholdere fylt ut. En plassholder uten verdi ennå vises som en tankestrek. */
export function loefte(config: HypothesisConfig | undefined, h: Hypothesis): string | null {
  const ord = config?.statusWording.find((w) => w.key === h.status);
  if (!ord) return null;
  const dato = h.deadlineAt ?? h.projectedDeadlineAt;
  const verdier: Record<string, string | undefined> = {
    nummer: h.number !== undefined ? `H-${h.number}` : undefined,
    dato: dato ? kortDatoAv(dato) : undefined,
    vurderer: h.reviewer ? navnAv(h.reviewer) : undefined,
    eier: h.owner ? navnAv(h.owner) : undefined,
    beslutningstaker: h.decider ? navnAv(h.decider) : undefined,
  };
  return fyllMal(ord.promise, verdier);
}

export function HypoteseSide({ nr }: { nr: number }) {
  const naviger = useNavigate();
  const sendt = Boolean((useLocation().state as { sendt?: boolean } | null)?.sendt);
  const hypotese = useHypoteseMedNummer(nr);
  const config = useHypoteseConfig();
  const tenant = useMe().data?.tenantName;
  const helt = useRef<HTMLParagraphElement>(null);
  const h = hypotese.data;

  useEffect(() => {
    if (sendt && h) helt.current?.focus();
  }, [sendt, h]);

  const tittel = sendt ? 'Sendt' : nummerTekst(nr);
  const fot = (
    <Fot
      hoyre={
        <button type="button" className="panel-knapp-sekundaer" onClick={() => void naviger(`/h/${nr}`, { replace: true })} data-testid="h-se-hypotesen">
          Se hypotesen
        </button>
      }
      primar={{ label: 'Ferdig', onClick: () => void naviger('/h/mine'), tast: 'enter', testid: 'h-ferdig' }}
    />
  );

  if (hypotese.isPending) {
    return (
      <Flate tittel={tittel} tilbake={{ navn: 'Mine hypoteser', til: '/h/mine' }} bredde="smal" midt>
        <div role="status" aria-busy="true" aria-label="Laster" className="panel-skjelett">
          <span className="panel-skjelett-linje panel-skjelett-tittel" />
          <span className="panel-skjelett-linje" />
        </div>
      </Flate>
    );
  }
  if (hypotese.isError || !h) {
    return (
      <Flate tittel={tittel} tilbake={{ navn: 'Mine hypoteser', til: '/h/mine' }} bredde="smal" midt>
        {/* K2: en hypotese som ikke finnes, sier det samme som en du ikke har tilgang til. */}
        <Varsel>{hypotese.isError && erNettfeil(hypotese.error) ? 'Fikk ikke hentet hypotesen. Sjekk nettet og prøv igjen.' : 'Fant ikke hypotesen. Den kan være slettet, eller ikke synlig for deg.'}</Varsel>
      </Flate>
    );
  }

  const status = config.data?.statusWording.find((w) => w.key === h.status)?.label;
  const frist = h.deadlineAt ?? h.projectedDeadlineAt;
  return (
    <Flate tittel={tittel} tilbake={{ navn: 'Mine hypoteser', til: '/h/mine' }} bredde="smal" midt fot={sendt ? fot : undefined}>
      <section aria-labelledby="h-kv-helt" className="h-kvittering" data-testid="h-kvittering">
        <div className="h-kv-hode">
          <span>{tenant ?? ' '}</span>
          <b data-testid="h-nummer">{nummerTekst(h.number)}</b>
        </div>
        <p id="h-kv-helt" ref={helt} tabIndex={-1} className="h-kv-helt">
          {loefte(config.data, h) ?? status ?? ' '}
        </p>
        <p className="h-kv-tittel">{h.title}</p>
        {h.status === 'mottatt' ? (
          <ol aria-label="Hvor hypotesen står" className="h-sporing">
            <li aria-current="step" className="h-sporing-ledd h-sporing-naa">
              <span className="h-sporing-node" aria-hidden="true" />
              <b>{status ?? 'Mottatt'}</b>
              {h.submittedAt ? <span className="h-sporing-under">{kortDatoAv(h.submittedAt)}</span> : null}
            </li>
            <li className="h-sporing-ledd">
              <span className="h-sporing-node h-sporing-node-neste" aria-hidden="true" />
              <b>{config.data?.statusWording.find((w) => w.key === 'vurderes')?.label ?? 'Vurderes'}</b>
              <span className="h-sporing-under">en vurderer tar den</span>
            </li>
            {frist ? (
              <li className="h-sporing-ledd h-sporing-helt">
                <span className="h-sporing-node h-sporing-node-neste" aria-hidden="true" />
                <b>Avgjørelse</b>
                <span className="h-sporing-under">innen {kortDatoAv(frist)} · når den tas</span>
              </li>
            ) : null}
          </ol>
        ) : status ? (
          <span className="h-pille h-pille-status">
            <Ikon navn="ellipse-outline" />
            {status}
          </span>
        ) : null}
      </section>
      {sendt ? <p className="h-hjelp">Kortet ligger i «Mine hypoteser» og oppdaterer seg selv.</p> : null}
    </Flate>
  );
}
